/**
 * KDX Shipping Calculator — Lógica compartida
 * Versión: 2.0
 *
 * Este archivo centraliza toda la lógica de zonas, fechas y almacenamiento
 * para ser reutilizada tanto en la product page como en la collection page.
 *
 * Cómo incluirlo:
 *   En theme.liquid (antes del </body>):
 *   {{ 'kdx-shipping.js' | asset_url | script_tag }}
 *
 * Expone: window.KDX
 */

window.KDX = window.KDX || (function () {

  // ─────────────────────────────────────────────
  // 1. RANGOS DE ZONAS
  // ─────────────────────────────────────────────
  const RANGES = {
    zone1: [   // Entrega rápida (1 día hábil base)
      [87000, 89999], // Tamaulipas
      [76000, 76999], // Querétaro
      [64000, 67999], // Nuevo León
      [43000, 43039], // Huejutla y colonias cercanas
      [78000, 79999], // San Luis Potosí
      [25000, 27999], // Coahuila
      [34000, 34999], // Durango
      [31000, 33999], // Chihuahua
      [20000, 20999], // Aguascalientes
      [98000, 99999], // Zacatecas
      [36000, 38999], // Guanajuato
      [93200, 93399], // Poza Rica
    ],
    zone2: [   // CDMX + Guadalajara (2 días hábiles base)
      [1000,  16999], // CDMX / Edomex
      [44000, 45999], // Guadalajara
    ],
    zone3: [   // Puebla / Toluca (3 días hábiles base)
      [72000, 75999], // Puebla
      [50000, 57999], // Edomex interior / Toluca
    ],
    // zone4 → todo lo demás (4 días hábiles base)
  };

  // Días de tránsito por zona: [rápido, máximo]
  const ZONE_DAYS = {
    zone1: [1, 4],
    zone2: [2, 5],
    zone3: [3, 7],
    zone4: [4, 8],
  };

  // ─────────────────────────────────────────────
  // 2. HELPERS DE FECHA
  // ─────────────────────────────────────────────

  function getZone(cp) {
    for (const z in RANGES) {
      for (const [a, b] of RANGES[z]) {
        if (cp >= a && cp <= b) return z;
      }
    }
    return 'zone4';
  }

  function pad2(n) { return String(n).padStart(2, '0'); }

  function formatDay(d) {
    return d.toLocaleDateString('es-MX', {
      weekday: 'short', day: 'numeric', month: 'short',
    });
  }

  function addBusinessDays(date, days) {
    const d = new Date(date);
    while (days > 0) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0) days--; // domingo no cuenta
    }
    return d;
  }

  // Cutoff: siempre 1:30 PM (uniforme Lun–Vie)
  function getCutoff(date) {
    const x = new Date(date);
    x.setHours(13, 30, 0, 0);
    return x;
  }

  /**
   * ¿Estamos en "modo fin de semana"?
   * Aplica desde el viernes 1:30 PM hasta el lunes 12:00 AM.
   */
  function isWeekendMode(now) {
    const day  = now.getDay();
    const h    = now.getHours();
    const m    = now.getMinutes();
    if (day === 5 && (h > 13 || (h === 13 && m >= 30))) return true;
    if (day === 6 || day === 0) return true;
    return false;
  }

  /**
   * Siguiente momento de recolección (próximo día hábil antes del cutoff).
   */
  function nextPickupMoment(now) {
    const d = new Date(now);

    // Dom → siguiente lunes al cutoff
    if (d.getDay() === 0) {
      d.setDate(d.getDate() + 1);
      return getCutoff(d);
    }
    // Sáb → siguiente lunes al cutoff
    if (d.getDay() === 6) {
      d.setDate(d.getDate() + 2);
      return getCutoff(d);
    }

    // Lun–Vie
    const todayCutoff = getCutoff(d);
    if (now <= todayCutoff) return todayCutoff;

    // Pasado el cutoff: siguiente día hábil
    d.setDate(d.getDate() + 1);
    while (d.getDay() === 0 || d.getDay() === 6) {
      d.setDate(d.getDate() + 1);
    }
    return getCutoff(d);
  }

  function timeLeftStr(now, limit) {
    const ms       = limit - now;
    const totalMin = Math.max(0, Math.floor(ms / 60000));
    const h        = Math.floor(totalMin / 60);
    const m        = totalMin % 60;
    return `${h} h ${pad2(m)} min`;
  }

  /**
   * Calcula fechas estimadas de entrega.
   * @param {string} zone
   * @param {Date}   now
   * @returns {{ fastDays, maxDays, fastest, latest, pickupLimit }}
   */
  function calcETA(zone, now) {
    const [fastDays, maxDays] = ZONE_DAYS[zone] || ZONE_DAYS.zone4;
    const pickupLimit  = nextPickupMoment(now);
    const baseTransit  = (now <= pickupLimit && pickupLimit.toDateString() === now.toDateString())
      ? now : pickupLimit;
    const fastest = addBusinessDays(baseTransit, fastDays);
    const latest  = addBusinessDays(baseTransit, maxDays);
    return { fastDays, maxDays, fastest, latest, pickupLimit };
  }

  /**
   * Construye el texto de headline de entrega rápida.
   * @param {string} zone
   * @param {Date}   now
   * @returns {string} HTML string
   */
  function buildHeadline(zone, now) {
    const { fastest, pickupLimit } = calcETA(zone, now);

    if (isWeekendMode(now)) {
      const WEEKEND_DAYS = { zone1: 2, zone2: 3, zone3: 4, zone4: 5 };
      const wd = WEEKEND_DAYS[zone] || 5;
      return `Entrega más rápida en <b>${wd} día${wd > 1 ? 's' : ''} hábil${wd > 1 ? 'es' : ''}</b>`;
    }

    // Zone1 y pedido antes del cutoff del mismo día: "Mañana [día]"
    if (zone === 'zone1' && now <= pickupLimit && pickupLimit.toDateString() === now.toDateString()) {
      const manana = addBusinessDays(now, 1);
      return `Entrega más rápida: <b>Mañana ${manana.toLocaleDateString('es-MX', { weekday: 'long' })}</b>`;
    }

    return `Entrega más rápida: <b>${formatDay(fastest)}</b>`;
  }

  // ─────────────────────────────────────────────
  // 3. ALMACENAMIENTO DEL C.P.
  // ─────────────────────────────────────────────
  const CP_KEY = 'kdx_cp';

  function saveCp(cp) {
    try { localStorage.setItem(CP_KEY, String(cp).padStart(5, '0')); } catch(e) {}
  }

  function getSavedCp() {
    try {
      const v = (localStorage.getItem(CP_KEY) || '').replace(/\D/g, '').slice(0, 5);
      return /^\d{5}$/.test(v) ? v : null;
    } catch(e) { return null; }
  }

  // ─────────────────────────────────────────────
  // 4. CALCULADORA EN PRODUCT PAGE
  // ─────────────────────────────────────────────
  /**
   * Inicializa la calculadora de C.P. en la página de producto.
   *
   * IDs esperados (configurables):
   *   inputId  → input del C.P.
   *   btnId    → botón "Calcular"
   *   resultId → contenedor del resultado
   */
  function initProductCalculator(opts) {
    opts = opts || {};
    const inputId  = opts.inputId  || 'kdx_cp_input';
    const btnId    = opts.btnId    || 'kdx_cp_btn';
    const resultId = opts.resultId || 'kdx_cp_result';

    const input  = document.getElementById(inputId);
    const btn    = document.getElementById(btnId);
    const result = document.getElementById(resultId);
    if (!input || !btn || !result) return;

    function renderResult(cp) {
      const now  = new Date();
      const zone = getZone(cp);
      const { fastest, latest, pickupLimit } = calcETA(zone, now);
      const headline = buildHeadline(zone, now);
      const left     = timeLeftStr(now, pickupLimit);

      result.innerHTML = `
        <div style="background:rgba(0,0,0,.04);padding:12px 14px;border-radius:14px;">
          <div style="color:#16a34a;font-weight:700;font-size:14px;">
            ● ${headline}
          </div>
          <div style="margin-top:6px;font-size:13px;">
            Comprando dentro de las próximas <b>${left}</b>
          </div>
          <div style="margin-top:6px;font-size:12px;opacity:.75;">
            Entrega estimada entre <b>${formatDay(fastest)}</b> y <b>${formatDay(latest)}</b>
          </div>
        </div>`;
      result.style.display = 'block';
      saveCp(cp);
    }

    btn.addEventListener('click', () => {
      const raw = (input.value || '').replace(/\D/g, '').slice(0, 5);
      input.value = raw;
      if (!/^\d{5}$/.test(raw)) {
        alert('Ingresa un código postal válido de 5 dígitos');
        return;
      }
      renderResult(parseInt(raw, 10));
    });

    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') { e.preventDefault(); btn.click(); }
    });

    // Pre-cargar C.P. guardado
    const saved = getSavedCp();
    if (saved) {
      input.value = saved;
      renderResult(parseInt(saved, 10));
    }
  }

  // ─────────────────────────────────────────────
  // 5. BARRA DE C.P. EN COLLECTION PAGE
  // ─────────────────────────────────────────────
  /**
   * Inicializa la barra de C.P. en la página de colección.
   * Solo guarda el C.P. y muestra un headline simplificado.
   * La fecha exacta se sigue calculando en el producto.
   */
  function initCollectionBar(opts) {
    opts = opts || {};
    const inputId  = opts.inputId  || 'kdx_col_cp_input';
    const btnId    = opts.btnId    || 'kdx_col_cp_btn';
    const resultId = opts.resultId || 'kdx_col_cp_result';

    const input  = document.getElementById(inputId);
    const btn    = document.getElementById(btnId);
    const result = document.getElementById(resultId);
    if (!input || !btn) return;

    function renderBar(cp) {
      saveCp(cp);
      if (!result) return;
      const zone     = getZone(cp);
      const now      = new Date();
      const headline = buildHeadline(zone, now);
      result.innerHTML = `
        <span style="color:#16a34a;font-weight:700;">● ${headline}</span>
        <span style="opacity:.7;font-size:12px;"> · Fecha exacta en cada producto.</span>`;
      result.style.display = 'inline-flex';
      result.style.alignItems = 'center';
      result.style.gap = '6px';
    }

    btn.addEventListener('click', () => {
      const raw = (input.value || '').replace(/\D/g, '').slice(0, 5);
      input.value = raw;
      if (!/^\d{5}$/.test(raw)) {
        alert('Ingresa un código postal válido de 5 dígitos');
        return;
      }
      renderBar(parseInt(raw, 10));
    });

    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') { e.preventDefault(); btn.click(); }
    });

    // Pre-cargar C.P. guardado
    const saved = getSavedCp();
    if (saved) {
      input.value = saved;
      renderBar(parseInt(saved, 10));
    }
  }

  // ─────────────────────────────────────────────
  // API PÚBLICA
  // ─────────────────────────────────────────────
  return {
    getZone,
    calcETA,
    buildHeadline,
    formatDay,
    isWeekendMode,
    saveCp,
    getSavedCp,
    initProductCalculator,
    initCollectionBar,
  };

})();